/**
 * AP-1106 - This file has anything to do with the new Campus Building.
 */

// Time defaults
export const DEFAULT_OPEN_TIME = "08:00";
export const DEFAULT_CLOSE_TIME = "17:00";
export const DEFAULT_DISABLED_START_TIME = "00:00";
export const DEFAULT_DISABLED_END_TIME = "23:59";

export const OPERATING_HOUR_DAYS = [
    { key: "monday", label: "Monday", short: "Mon" },
    { key: "tuesday", label: "Tuesday", short: "Tue" },
    { key: "wednesday", label: "Wednesday", short: "Wed" },
    { key: "thursday", label: "Thursday", short: "Thu" },
    { key: "friday", label: "Friday", short: "Fri" },
    { key: "saturday", label: "Saturday", short: "Sat" },
    { key: "sunday", label: "Sunday", short: "Sun" }
];

export const DEFAULT_OPERATING_HOURS = {
    monday: { enabled: true, ranges: [{ start: DEFAULT_OPEN_TIME, end: DEFAULT_CLOSE_TIME }] },
    tuesday: { enabled: true, ranges: [{ start: DEFAULT_OPEN_TIME, end: DEFAULT_CLOSE_TIME }] },
    wednesday: { enabled: true, ranges: [{ start: DEFAULT_OPEN_TIME, end: DEFAULT_CLOSE_TIME }] },
    thursday: { enabled: true, ranges: [{ start: DEFAULT_OPEN_TIME, end: DEFAULT_CLOSE_TIME }] },
    friday: { enabled: true, ranges: [{ start: DEFAULT_OPEN_TIME, end: DEFAULT_CLOSE_TIME }] },
    saturday: { enabled: false, ranges: [{ start: DEFAULT_DISABLED_START_TIME, end: DEFAULT_DISABLED_END_TIME }] },
    sunday: { enabled: false, ranges: [{ start: DEFAULT_DISABLED_START_TIME, end: DEFAULT_DISABLED_END_TIME }] }
};

export const ROOM_TYPE_OPTIONS = [
    { value: "Practical Venue", label: "Practical Venue" },
    { value: "Classroom", label: "Classroom" },
    { value: "Studio", label: "Studio" },
    { value: "Boardroom", label: "Boardroom" },
    { value: "Multi-Purpose Hall", label: "Multi-Purpose Hall" }
];

/**
 * Returns a cloned operating-hours object to prevent mutation of shared defaults.
 */
export const cloneOperatingHours = (hours = DEFAULT_OPERATING_HOURS) => {
    return Object.keys(hours).reduce((acc, day) => {
        const currentDay = hours[day] || { enabled: false, ranges: [] };

        acc[day] = {
            enabled: Boolean(currentDay.enabled),
            ranges: (currentDay.ranges || []).map((range) => ({
                start: range.start,
                end: range.end
            }))
        };

        return acc;
    }, {});
};

/**
 * Returns the default operating-hours object (cloned to avoid mutation).
 */
export const getDefaultOperatingHours = () => cloneOperatingHours(DEFAULT_OPERATING_HOURS);

/**
 * Returns updated operating hours for a day toggle (pure function, no mutation).
 */
export const updateOperatingHoursByDay = ({
    operatingHours,
    day,
    enabled,
    timeRange
}) => {
    const nextOperatingHours = { ...operatingHours };

    const currentDay = nextOperatingHours[day] || {
        enabled: false,
        ranges: [{ start: DEFAULT_OPEN_TIME, end: DEFAULT_CLOSE_TIME }]
    };

    const currentRange = currentDay.ranges?.[0] || {
        start: DEFAULT_OPEN_TIME,
        end: DEFAULT_CLOSE_TIME
    };

    if (!enabled) {
        nextOperatingHours[day] = {
            enabled: false,
            ranges: [
                {
                    start: currentRange.start || DEFAULT_DISABLED_START_TIME,
                    end: currentRange.end || DEFAULT_DISABLED_END_TIME
                }
            ]
        };

        return nextOperatingHours;
    }

    const [start = currentRange.start || DEFAULT_OPEN_TIME, end = currentRange.end || DEFAULT_CLOSE_TIME] =
        (timeRange || `${currentRange.start || DEFAULT_OPEN_TIME}-${currentRange.end || DEFAULT_CLOSE_TIME}`).split("-");

    nextOperatingHours[day] = {
        enabled: true,
        ranges: [{ start, end }]
    };

    return nextOperatingHours;
};

/**
 * Returns updated operating hours for a time field change (pure function, no mutation).
 */
export const updateOperatingHoursTimeField = ({
    operatingHours,
    day,
    field,
    value
}) => {
    const nextOperatingHours = { ...operatingHours };

    const currentDay = nextOperatingHours[day] || {
        enabled: true,
        ranges: [{ start: DEFAULT_OPEN_TIME, end: DEFAULT_CLOSE_TIME }]
    };

    const currentRange = currentDay.ranges?.[0] || {
        start: DEFAULT_OPEN_TIME,
        end: DEFAULT_CLOSE_TIME
    };

    nextOperatingHours[day] = {
        enabled: currentDay.enabled,
        ranges: [
            {
                ...currentRange,
                [field]: value
            }
        ]
    };

    return nextOperatingHours;
};

/**
 * Returns display text for a single day's operating hours range.
 */
export const getOperatingHoursRangeText = (dayData) => {
    if (!dayData || !dayData.enabled || !dayData.ranges || !dayData.ranges.length) {
        return "";
    }

    const firstRange = dayData.ranges[0];
    return `${firstRange.start || DEFAULT_OPEN_TIME} - ${firstRange.end || DEFAULT_CLOSE_TIME}`;
};

/**
 * Returns grouped operating hours rows for table display (e.g., "Mon-Fri 08:00-17:00").
 */
export const getOperatingHoursRows = (hours) => {
    hours = parseOperatingHoursString(hours);

    if (!hours || typeof hours !== "object") {
        return [];
    }

    const enabledDays = OPERATING_HOUR_DAYS.filter(
        (day) => hours[day.key] && hours[day.key].enabled
    );

    if (!enabledDays.length) {
        return [];
    }

    const rows = [];
    let currentGroup = [];

    const flushGroup = () => {
        if (!currentGroup.length) return;

        const first = currentGroup[0];
        const last = currentGroup[currentGroup.length - 1];
        const label = currentGroup.length === 1 ? first.short : `${first.short}-${last.short}`;
        const time = getOperatingHoursRangeText(hours[first.key]);

        rows.push({ label, time });
        currentGroup = [];
    };

    enabledDays.forEach((day, index) => {
        if (!currentGroup.length) {
            currentGroup.push(day);
        } else {
            const previousDay = currentGroup[currentGroup.length - 1];
            const previousIndex = OPERATING_HOUR_DAYS.findIndex((item) => item.key === previousDay.key);
            const currentIndex = OPERATING_HOUR_DAYS.findIndex((item) => item.key === day.key);
            const previousTime = getOperatingHoursRangeText(hours[previousDay.key]);
            const currentTime = getOperatingHoursRangeText(hours[day.key]);

            if (currentIndex === previousIndex + 1 && previousTime === currentTime) {
                currentGroup.push(day);
            } else {
                flushGroup();
                currentGroup.push(day);
            }
        }

        if (index === enabledDays.length - 1) {
            flushGroup();
        }
    });

    return rows;
};

/**
 * Format operating hours object to string for API/display.
 */
export const formatOperatingHoursForDisplay = (hours) => {
    if (!hours) return "";

    const rows = getOperatingHoursRows(hours);
    return rows.map((row) => `${row.label} ${row.time}`).join(" | ");
};

/**
 * Parse operating hours string to object (for mock data).
 * TODO: Change this functionality so that it doesn't use range: [] array.
 * Rather use it the same way that the API sends it { "day_of_week": "Monday", "enabled": 1, "open_time": "08:01:00", "close_time": "15:00:00" }
 * Remember to add defaults to items that don't get sent.
 */
export const parseOperatingHoursString = (operatingHours) => {
    if (!operatingHours) {
        return null;
    }

    // Already parsed into frontend shape
    if (
        typeof operatingHours === "object" &&
        !Array.isArray(operatingHours)
    ) {
        return operatingHours;
    }

    const parsedHours = getDefaultOperatingHours();

    // Reset all days
    Object.keys(parsedHours).forEach((day) => {
        parsedHours[day] = {
            enabled: false,
            ranges: []
        };
    });

    // Handle schedule array from API
    if (Array.isArray(operatingHours)) {

        operatingHours.forEach((schedule) => {

            if (!schedule?.day_of_week) return;

            const dayKey = schedule.day_of_week.toLowerCase();

            parsedHours[dayKey] = {
                enabled: Boolean(schedule.enabled),
                ranges:
                    schedule.enabled &&
                        schedule.open_time &&
                        schedule.close_time
                        ? [
                            {
                                start: schedule.open_time.slice(0, 5),
                                end: schedule.close_time.slice(0, 5)
                            }
                        ]
                        : []
            };
        });
    }

    return parsedHours;
};

/**
 * Centralized helper for shaping building/room data specifically to use the operating_hours.
 * @param {*} data Building or Room
 * @returns Returns mapped data with usable operatingHoursObj.
 */
export const mapBuildingOrRoomsForTable = (data) => ({
    ...data,
    operatingHoursObj: parseOperatingHoursString(data.operating_hours)
});

export const normalizeOperatingHours = (operatingHours = {}) => {
    return Object.keys(operatingHours).map((dayKey) => {
        const dayData = operatingHours[dayKey] || {};
        const firstRange = dayData?.ranges?.[0];

        return {
            day_of_week: dayKey,
            enabled: dayData?.enabled ? 1 : 0,
            open_time: firstRange?.start
                ? `${firstRange.start.length === 5 ? firstRange.start : firstRange.start.slice(0, 5)}:00`
                : null,
            close_time: firstRange?.end
                ? `${firstRange.end.length === 5 ? firstRange.end : firstRange.end.slice(0, 5)}:00`
                : null,
        };
    });
};

/**
 * Helper to get days from label like "Mon-Fri" or "Sat".
 */
export const getDaysFromLabel = (label) => {
    const normalizedLabel = label.trim();

    if (normalizedLabel.includes("-")) {
        const [startLabel, endLabel] = normalizedLabel.split("-").map((value) => value.trim());
        const startIndex = OPERATING_HOUR_DAYS.findIndex((day) => day.short === startLabel);
        const endIndex = OPERATING_HOUR_DAYS.findIndex((day) => day.short === endLabel);

        if (startIndex === -1 || endIndex === -1 || endIndex < startIndex) {
            return [];
        }

        return OPERATING_HOUR_DAYS.slice(startIndex, endIndex + 1).map((day) => day.key);
    }

    const exactDay = OPERATING_HOUR_DAYS.find((day) => day.short === normalizedLabel);
    return exactDay ? [exactDay.key] : [];
};

/**
 * Maps the buildings to be used on campus pages to [{label,value}] pairs.
 * It filters out null values.
 * @param {*} buildings Array ob buildings [{id,name}]
 * @returns [{label,value}]
 */
export function mapBuildingsToSelect(buildings) {
    return buildings?.map(mapSingleBuildingToSelect) ?? [];
}

/**
 * Only maps a single building to Select pairing.
 * @param {*} building Building to map.
 * @returns Returns an Select-compatible building.
 */
export function mapSingleBuildingToSelect(building) {
    if (!building) {
        return null;
    }
    return {
        value: Number(building?.id ?? 0),
        label: building?.name,
        ...(building?.grouping_color && {
            grouping_color: building?.grouping_color
        }),
        ...(building?.campus_id && {
            campus_id: building?.campus_id
        })
    };
}


/**
 * Maps a SELECT (value-label pair) to a payload the API can use.
 * @param {Array<object>} buildings Buildings.
 * @returns Returns an array of Buildings the API can use.
 */
export function mapBuildingsToApi(buildings) {
    if (buildings?.length > 0) {
        buildings = buildings.filter(a => a);
    }
    return buildings?.map(mapSingleBuildingToAPI) ?? [];
}

/**
 * Only maps a single building to API.
 * @param {*} building Building to map.
 * @returns Returns an API-compatible building.
 */
export function mapSingleBuildingToAPI(building) {
    if (!building) {
        return null;
    }
    return {
        id: building.value,
        name: building.label,
        ...(building.grouping_color && {
            grouping_color: building.grouping_color
        }),
        ...(building.campus_id && {
            campus_id: building.campus_id
        })
    };
}
